/**
 * Admin User Management - User list with detail modal (matches Helply reference design)
 */
import { useLanguage } from "@/context/AppContext";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, ChevronLeft, Mail, Phone, User, X, CheckCircle, Calendar, Ban, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUB_STATUS_BADGE = {
  annual: { label: 'Active', color: 'bg-green-100 text-green-700' },
  monthly: { label: 'Active', color: 'bg-green-100 text-green-700' },
  trial: { label: 'Trial', color: 'bg-blue-100 text-blue-700' },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700' },
  promo: { label: 'Promo', color: 'bg-purple-100 text-purple-700' },
};

const InfoRow = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
    <p className="text-sm text-gray-800 font-medium">{value || '—'}</p>
  </div>
);

const SectionTitle = ({ children }) => (
  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest border-b pb-2 mb-3">{children}</p>
);

const AdminUserManagement = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState(
    searchParams.get('filter') === 'pending_verification' ? 'pending' : 'all'
  );
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/users?page=${page}&limit=20`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (roleFilter && roleFilter !== 'all') {
        if (roleFilter === 'pending') {
          url = `${API}/admin/verifications`;
        } else {
          url += `&role=${roleFilter}`;
        }
      }
      const res = await axios.get(url, { withCredentials: true });
      if (roleFilter === 'pending') {
        setUsers(res.data.providers || []);
        setTotalPages(1);
      } else {
        setUsers(res.data.users || []);
        setTotalPages(res.data.pages || 1);
      }
    } catch {
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  }, [page, roleFilter, searchQuery]);

  useEffect(() => { fetchUsers(); }, [page, roleFilter]);

  const openUserModal = async (user) => {
    setSelectedUser(user);
    setModalLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${user.user_id}`, { withCredentials: true });
      setSelectedUser(res.data);
    } catch {
      // use basic user data
    } finally {
      setModalLoading(false);
    }
  };

  const doAction = async (action) => {
    if (!selectedUser) return;
    setActionLoading(action);
    try {
      const uid = selectedUser.user_id;
      if (action === 'active_yearly') {
        await axios.post(`${API}/admin/users/${uid}/grant-free-subscription`, {}, { withCredentials: true });
        toast.success('Annual subscription activated!');
      } else if (action === 'trial') {
        await axios.post(`${API}/admin/users/${uid}/set-trial`, {}, { withCredentials: true });
        toast.success('30-day trial set!');
      } else if (action === 'block') {
        if (!window.confirm('Block this user?')) return;
        await axios.post(`${API}/admin/users/${uid}/block`, {}, { withCredentials: true });
        toast.success('User blocked');
      } else if (action === 'unblock') {
        await axios.post(`${API}/admin/users/${uid}/unblock`, {}, { withCredentials: true });
        toast.success('User unblocked');
      } else if (action === 'verify') {
        await axios.post(`${API}/admin/verify-user/${uid}`, {}, { withCredentials: true });
        toast.success('User verified!');
      } else if (action === 'delete') {
        if (!window.confirm('Delete this user permanently?')) return;
        await axios.delete(`${API}/admin/users/${uid}`, { withCredentials: true });
        toast.success('User deleted');
        setSelectedUser(null);
      } else if (action === 'make_admin') {
        if (!window.confirm(`Promote ${selectedUser.name} to admin?`)) return;
        await axios.post(`${API}/admin/make-admin/${uid}`, {}, { withCredentials: true });
        toast.success('Admin privileges granted');
      }
      // Refresh user data
      const res = await axios.get(`${API}/admin/users/${uid}`, { withCredentials: true }).catch(() => null);
      if (res) setSelectedUser(res.data);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Diesen Benutzer wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('Benutzer gelöscht');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const promoteToAdmin = async (user) => {
    if (!window.confirm(`${user.name} (${user.email}) zum Admin befördern? Dieser Benutzer erhält vollen Admin-Zugriff.`)) return;
    try {
      await axios.post(`${API}/admin/make-admin/${user.user_id}`, {}, { withCredentials: true });
      toast.success('Admin-Rechte erteilt');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('de-CH', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const getSubStatus = (user) => {
    const sub = user.subscription;
    if (!sub) return null;
    return SUB_STATUS_BADGE[sub] || { label: sub, color: 'bg-gray-100 text-gray-600' };
  };

  return (
    <div className="min-h-screen infirmly-dark infirmly-dark flex flex-col" style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }} data-testid="admin-user-management">
      {/* Header */}
      <header className="border-b border-white/10 sticky bg-white/[0.04] backdrop-blur top-0 z-40 px-6 py-4 flex items-center gap-4">
        <User className="w-5 h-5 text-[#0044CC]" />
        <h1 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('userManagement')}</h1>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="ml-auto gap-2 rounded-lg">
          <ChevronLeft className="w-4 h-4" /> Dashboard
        </Button>
      </header>

      {/* Search bar */}
      <div className="border-b border-white/10 bg-white/[0.04] px-6 py-3">
        <div className="flex gap-3 max-w-2xl">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Name oder E-Mail suchen..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchUsers()}
              className="pl-10 rounded-lg"
              data-testid="user-search-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); setPage(1); }}>
            <SelectTrigger className="w-44 rounded-lg" data-testid="user-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('adminFilterAllUsers')}</SelectItem>
              <SelectItem value="client">{t('adminFilterClients')}</SelectItem>
              <SelectItem value="provider">{t('adminFilterProviders')}</SelectItem>
              <SelectItem value="pending">Ausstehende Verifizierung</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchUsers} className="bg-[#0044CC] hover:bg-[#003399] rounded-lg" data-testid="user-search-btn">
            {t('adminSearchBtn')}
          </Button>
        </div>
      </div>

      <main className="flex-1 overflow-y-auto">
        {/* User count */}
        <div className="px-6 py-4">
          <p className="text-sm font-medium text-gray-500 flex items-center gap-2">
            <User className="w-4 h-4" />
            Benutzer ({users.length})
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16 text-gray-400">Keine Benutzer gefunden</div>
        ) : (
          <div className="px-4 space-y-2 pb-8">
            {users.map(user => (
              <div
                key={user.user_id}
                className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3 hover:border-blue-200 transition-colors"
              >
                {/* Avatar */}
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <AvatarImage src={user.profile_picture} />
                  <AvatarFallback
                    className="text-white font-semibold text-sm"
                    style={{ backgroundColor: user.role === 'provider' ? '#0044CC' : '#6B7280' }}
                  >
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-gray-900 truncate">{user.name}</span>
                    {user.verified && (
                      <Badge className="bg-green-100 text-green-700 text-xs px-1.5 py-0">{t('adminVerifiedBadge')}</Badge>
                    )}
                    {user.is_admin && (
                      <Badge className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0">Admin</Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <Mail className="w-3 h-3" /> {user.email}
                  </p>
                  {user.phone && (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" /> {user.phone}
                    </p>
                  )}
                </div>

                {/* Role badge */}
                <Badge
                  className={`text-xs flex-shrink-0 ${user.role === 'provider' ? 'bg-[#0044CC] text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  {user.role === 'provider' ? t('adminRoleExpert') : t('adminRoleClient')}
                </Badge>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => openUserModal(user)}
                    className="w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-100 flex items-center justify-center transition-colors"
                    data-testid={`edit-user-${user.user_id}`}
                    title="Details anzeigen"
                  >
                    <svg className="w-4 h-4 text-[#0044CC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                  <button
                    className="w-8 h-8 rounded-lg bg-orange-50 hover:bg-orange-100 flex items-center justify-center transition-colors"
                    title="Abonnement"
                    onClick={() => openUserModal(user)}
                  >
                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
                    </svg>
                  </button>
                  {!user.is_admin && (
                    <button
                      onClick={() => promoteToAdmin(user)}
                      className="w-8 h-8 rounded-lg bg-purple-50 hover:bg-purple-100 flex items-center justify-center transition-colors"
                      data-testid={`promote-admin-${user.user_id}`}
                      title="Zum Admin befördern"
                    >
                      <Crown className="w-4 h-4 text-purple-600" />
                    </button>
                  )}
                  {!user.is_admin && (
                    <button
                      onClick={() => deleteUser(user.user_id)}
                      className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
                      data-testid={`delete-user-${user.user_id}`}
                      title="Löschen"
                    >
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 pb-8">
            <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-lg">
              Zurück
            </Button>
            <span className="flex items-center px-4 text-sm text-gray-500">Seite {page} von {totalPages}</span>
            <Button variant="outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="rounded-lg">
              Weiter
            </Button>
          </div>
        )}
      </main>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="user-detail-modal">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-start justify-between p-6 border-b">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={selectedUser.profile_picture} />
                    <AvatarFallback
                      className="text-white font-semibold"
                      style={{ backgroundColor: selectedUser.role === 'provider' ? '#0044CC' : '#6B7280' }}
                    >
                      {selectedUser.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h2 className="font-bold text-gray-900">{selectedUser.name}</h2>
                    <p className="text-sm text-gray-500">{selectedUser.email}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {modalLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#0044CC]" />
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {/* ACCOUNT */}
                <div>
                  <SectionTitle>{t('adminAccount')}</SectionTitle>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoRow label={t('adminUserId')} value={selectedUser.user_id?.slice(0, 18) + '...'} />
                    <InfoRow label={t('role')} value={selectedUser.is_admin ? t('adminRoleAdmin') : selectedUser.role === 'provider' ? t('adminRoleExpert') : t('adminRoleClient')} />
                    <InfoRow label={t('adminJoined')} value={formatDate(selectedUser.created_at)} />
                    <InfoRow label={t('adminPhone')} value={selectedUser.phone} />
                  </div>
                </div>

                {/* SUBSCRIPTION */}
                <div>
                  <SectionTitle>{t('subscription')}</SectionTitle>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{t('adminColStatus')}</p>
                      {selectedUser.subscription ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getSubStatus(selectedUser)?.color || 'bg-gray-100 text-gray-600'}`}>
                          {getSubStatus(selectedUser)?.label || selectedUser.subscription}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-800">—</span>
                      )}
                    </div>
                    <InfoRow label={t('adminBillingCycle')} value={selectedUser.subscription === 'annual' ? t('adminPlanYearly') : selectedUser.subscription === 'monthly' ? t('adminPlanMonthly') : null} />
                    <InfoRow label={t('adminExpires')} value={formatDate(selectedUser.subscription_expires)} />
                    <InfoRow label={t('adminAutoRenew')} value={selectedUser.subscription_free ? t('adminLifetime') : t('adminOff')} />
                  </div>
                </div>

                {/* CREDITS & USAGE */}
                <div>
                  <SectionTitle>{t('adminCreditsUsage')}</SectionTitle>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoRow label={t('adminCredits')} value={selectedUser.credits} />
                    <InfoRow label={t('adminPromoUsed')} value={selectedUser.promo_code_used} />
                  </div>
                </div>

                {/* PROFILE & PREFERENCES */}
                <div>
                  <SectionTitle>{t('adminProfilePrefs')}</SectionTitle>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoRow label={t('adminLanguageLabel')} value={selectedUser.language?.toUpperCase()} />
                    <InfoRow label={t('adminCanton')} value={selectedUser.canton} />
                    <InfoRow label={t('adminNationality')} value={selectedUser.nationality} />
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{t('adminVerified')}</p>
                      <span className={`text-sm font-medium ${selectedUser.verified ? 'text-green-600' : 'text-red-500'}`}>
                        {selectedUser.verified ? t('adminYes') : t('adminNo')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* ADMIN ACTIONS */}
                <div>
                  <SectionTitle>{t('adminAdminActions')}</SectionTitle>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => doAction('active_yearly')}
                      disabled={actionLoading === 'active_yearly'}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-lg gap-2 flex-1"
                      data-testid="action-set-active"
                    >
                      {actionLoading === 'active_yearly'
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <CheckCircle className="w-3 h-3" />
                      }
                      {t('adminSetActiveYearly')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => doAction('trial')}
                      disabled={actionLoading === 'trial'}
                      className="bg-[#0044CC] hover:bg-[#003399] text-white rounded-lg gap-2 flex-1"
                      data-testid="action-set-trial"
                    >
                      {actionLoading === 'trial'
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Calendar className="w-3 h-3" />
                      }
                      {t('adminSetTrial')}
                    </Button>
                    {!selectedUser.is_admin && (
                      <Button
                        size="sm"
                        onClick={() => doAction(selectedUser.is_blocked ? 'unblock' : 'block')}
                        disabled={actionLoading === 'block' || actionLoading === 'unblock'}
                        className={`rounded-lg gap-2 flex-1 ${selectedUser.is_blocked ? 'bg-gray-500 hover:bg-gray-600' : 'bg-orange-500 hover:bg-orange-600'} text-white`}
                        data-testid="action-block-user"
                      >
                        {(actionLoading === 'block' || actionLoading === 'unblock')
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Ban className="w-3 h-3" />
                        }
                        {selectedUser.is_blocked ? t('adminUnblockUser') : t('adminBlockUser')}
                      </Button>
                    )}
                    {!selectedUser.is_admin && (
                      <Button
                        size="sm"
                        onClick={() => doAction('make_admin')}
                        disabled={actionLoading === 'make_admin'}
                        className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg gap-2 flex-1"
                        data-testid="action-make-admin"
                      >
                        {actionLoading === 'make_admin'
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Crown className="w-3 h-3" />
                        }
                        {t('adminMakeAdmin')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;
